import { useState } from 'react';
import { KeyRound } from 'lucide-react';
import { Key } from '@/types';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Modal } from '@/components/modal';
import { useNotification } from '@/components/notifications';
import { Label } from '@/components/ui/label';
import { DialogClose } from '@/components/ui/dialog';
import { LoadingSpinner } from '@/components/loading-spinner';
import { Textarea } from '@/components/ui/textarea';
import { useGenerateApiKey } from '@/hooks/mutations/keys/use-generate-api-key';
import { useQueryClient } from '@tanstack/react-query';
import { useOrgStore } from '@/stores/org-store';
import { useEditCapitolKey } from '@/hooks/mutations/keys/use-edit-capitol-key';

interface NameModalProps {
  isEdit?: boolean;
  description?: string;
  keyId?: string;
  isOpen: boolean;
  setIsApikeyModalOpen: (isModalOpen: boolean) => void;
  editKey?: ({ id, description }: { id: string; description: string }) => void;
  addKeyCallback?: (key: Key) => void;
}

export const AddCapitolKeyModal = ({
  setIsApikeyModalOpen,
  isEdit = false,
  description: descriptionProp = '',
  keyId = '',
  isOpen = false,
}: NameModalProps) => {
  const notification = useNotification();
  const queryClient = useQueryClient();
  const [description, setDescription] = useState(descriptionProp);
  const [name, setName] = useState('');
  const title = isEdit
    ? 'Edit Capitol API key'
    : 'Generate new Capitol API key';

  const { currentOrg } = useOrgStore();
  const orgId = currentOrg?.orgid || '';

  const handleSuccess = (action: 'created' | 'edited') => {
    void queryClient.invalidateQueries({ queryKey: ['capitol-keys', orgId] });
    setIsApikeyModalOpen(false);
    notification.success(`Key was ${action} successfully`);
    setDescription('');
    setName('');
  };

  const { mutate: generateApiKey, isPending: isGeneratingApiKey } =
    useGenerateApiKey({
      onSuccess: () => handleSuccess('created'),
      onError: (error) => {
        notification.error(error.message || 'An unexpected error occurred');
      },
    });

  const { mutate: editCapitolKey, isPending: isEditingApiKey } =
    useEditCapitolKey({
      onSuccess: () => handleSuccess('edited'),
      onError: (error) => {
        notification.error(error.message || 'An unexpected error occurred');
      },
    });

  const handleSave = () => {
    if (isEdit) {
      editCapitolKey({
        id: keyId,
        name: name?.trim(),
        description: description?.trim(),
      });
    } else {
      generateApiKey({
        name: name?.trim(),
        description: description?.trim(),
      });
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!description?.trim()) {
      return;
    }

    if (event.key === 'Enter') {
      handleSave();
    }
  };

  const Footer = () => (
    <>
      <DialogClose asChild>
        <Button id='api-keys-modal-cancel' variant='outline'>
          Cancel
        </Button>
      </DialogClose>
      <Button
        id='api-keys-modal-save'
        disabled={!name?.trim() || isEditingApiKey || isGeneratingApiKey}
        onClick={handleSave}
      >
        {isEditingApiKey || isGeneratingApiKey ? (
          <>
            <LoadingSpinner /> Saving..
          </>
        ) : (
          `Save`
        )}
      </Button>
    </>
  );

  const Title = () => (
    <div className='flex items-center gap-2'>
      <KeyRound />
      {title}
    </div>
  );

  return (
    <Modal
      isDialogOpen={isOpen}
      setIsDialogOpen={(value) => {
        setDescription('');
        setName('');
        setIsApikeyModalOpen(value);
      }}
      title={<Title />}
      footer={<Footer />}
    >
      <div className='mt-2'>
        <div className='mb-3'>
          <Label
            htmlFor='name'
            className='label-required text-sm font-medium mb-2'
          >
            Name
          </Label>
          <Input
            onChange={(event) => setName(event.target.value)}
            onKeyDown={handleKeyDown}
            value={name}
            id='api-keys-name-input'
            placeholder='i.e. dev-testing-key-01'
            autoFocus
          />
        </div>
        <div className='mb-3'>
          <Label
            htmlFor='api-keys-description-input'
            className='mb-2 text-sm font-medium'
          >
            Description
          </Label>
          <Textarea
            onChange={(event) => setDescription(event.target.value)}
            value={description}
            id='api-keys-description-input'
            rows={3}
          />
        </div>
      </div>
    </Modal>
  );
};
